import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import {
  User, Mail, Calendar, Shield, Loader, Eye, EyeOff,
  Save, Trash2, Key, AlertCircle
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const LEARNING_GOALS = [
  'Connect with family',
  'Business communication',
  'Cultural roots',
  'Travel'
];

const Profile = () => {
  const { user, updateProfile, logout } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Profile form state
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    learningGoals: user?.learning_goals || []
  });

  // Password change state
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState(false);

  // Delete account state
  const [deletePassword, setDeletePassword] = useState('');
  
  // Cancel subscription state
  const [showCancelSubscription, setShowCancelSubscription] = useState(false);

  const handleProfileUpdate = async () => {
    setLoading(true);

    const result = await updateProfile({
      name: profileData.name,
      learning_goals: profileData.learningGoals
    });

    if (result.success) {
      toast({
        title: "Success",
        description: "Profile updated successfully",
      });
      setEditing(false);
    } else {
      toast({
        title: "Error",
        description: result.error,
        variant: "destructive"
      });
    }

    setLoading(false);
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast({
        title: "Error",
        description: "New passwords do not match",
        variant: "destructive"
      });
      setLoading(false);
      return;
    }

    try {
      await axios.put(`${API_URL}/api/auth/me/password`, null, {
        params: {
          current_password: passwordData.currentPassword,
          new_password: passwordData.newPassword
        }
      });

      toast({
        title: "Success",
        description: "Password changed successfully",
      });

      setShowPasswordForm(false);
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to change password",
        variant: "destructive"
      });
    }

    setLoading(false);
  };

  const handleCancelSubscription = async () => {
    setLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/subscription/cancel`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        }
      );

      toast({
        title: "Success",
        description: "Your subscription has been cancelled. You'll have access until the end of your billing period.",
      });

      setShowCancelSubscription(false);
      
      // Optionally log out or refresh
      setTimeout(() => {
        window.location.reload();
      }, 2000);

    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.response?.data?.detail || "Failed to cancel subscription",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    setLoading(true);

    try {
      await axios.delete(`${API_URL}/api/auth/me`, {
        params: { password: deletePassword }
      });

      toast({
        title: "Account Deleted",
        description: "Your account has been permanently deleted",
      });

      logout();
      navigate('/');
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to delete account",
        variant: "destructive"
      });
    }

    setLoading(false);
  };

  const toggleGoal = (goal) => {
    setProfileData(prev => ({
      ...prev,
      learningGoals: prev.learningGoals.includes(goal)
        ? prev.learningGoals.filter(g => g !== goal)
        : [...prev.learningGoals, goal]
    }));
  };

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-red-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 font-heading mb-2">
            My Profile
          </h1>
          <p className="text-gray-600">Manage your account settings and preferences</p>
        </div>

        {/* Account Overview Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center">
                <User className="w-5 h-5 mr-2" />
                Account Information
              </span>
              {user.is_admin && (
                <Badge variant="destructive">
                  <Shield className="w-3 h-3 mr-1" />
                  Admin
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-600">Email Address</Label>
                <div className="flex items-center mt-1">
                  <Mail className="w-4 h-4 mr-2 text-gray-400" />
                  <span className="text-gray-900">{user.email}</span>
                </div>
              </div>

              <div>
                <Label className="text-gray-600">Account Type</Label>
                <div className="mt-1">
                  <Badge variant="outline" className="capitalize">
                    {user.auth_provider === 'google' ? '🔍 Google' : '📧 Email'}
                  </Badge>
                </div>
              </div>

              <div>
                <Label className="text-gray-600">Verification Status</Label>
                <div className="mt-1">
                  {user.is_verified ? (
                    <Badge className="bg-green-100 text-green-800 border-green-200">
                      ✓ Verified
                    </Badge>
                  ) : (
                    <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">
                      ⚠ Unverified
                    </Badge>
                  )}
                </div>
              </div>

              <div>
                <Label className="text-gray-600">Member Since</Label>
                <div className="flex items-center mt-1">
                  <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                  <span className="text-gray-900">
                    {new Date(user.created_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Profile Edit Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Personal Information</CardTitle>
            <CardDescription>Update your name and learning goals</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                value={editing ? profileData.name : user.name}
                onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                disabled={!editing}
              />
            </div>

            <div className="space-y-2">
              <Label>Learning Goals</Label>
              <div className="grid grid-cols-2 gap-3">
                {LEARNING_GOALS.map(goal => (
                  <div key={goal} className="flex items-center space-x-2">
                    <Checkbox
                      id={goal}
                      checked={(editing ? profileData.learningGoals : user.learning_goals).includes(goal)}
                      onCheckedChange={() => editing && toggleGoal(goal)}
                      disabled={!editing}
                    />
                    <label
                      htmlFor={goal}
                      className={`text-sm font-medium leading-none ${editing ? 'cursor-pointer' : 'cursor-default'}`}
                    >
                      {goal}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-2 pt-4">
              {!editing ? (
                <Button onClick={() => setEditing(true)}>
                  Edit Profile
                </Button>
              ) : (
                <>
                  <Button
                    onClick={handleProfileUpdate}
                    disabled={loading}
                    className="bg-gradient-to-r from-green-600 to-red-600 hover:from-green-700 hover:to-red-700"
                  >
                    {loading ? (
                      <>
                        <Loader className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Changes
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setEditing(false);
                      setProfileData({
                        name: user.name,
                        learningGoals: user.learning_goals
                      });
                    }}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Password Change Card (Only for email users) */}
        {user.auth_provider === 'email' && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Key className="w-5 h-5 mr-2" />
                Change Password
              </CardTitle>
              <CardDescription>Update your account password</CardDescription>
            </CardHeader>
            <CardContent>
              {!showPasswordForm ? (
                <Button onClick={() => setShowPasswordForm(true)} variant="outline">
                  Change Password
                </Button>
              ) : (
                <form onSubmit={handlePasswordChange} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="currentPassword">Current Password</Label>
                    <div className="relative">
                      <Input
                        id="currentPassword"
                        type={showPasswords ? "text" : "password"}
                        value={passwordData.currentPassword}
                        onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords(!showPasswords)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {showPasswords ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="newPassword">New Password</Label>
                    <Input
                      id="newPassword"
                      type={showPasswords ? "text" : "password"}
                      value={passwordData.newPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                      required
                      minLength={8}
                      placeholder="Min 8 characters"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirm New Password</Label>
                    <Input
                      id="confirmPassword"
                      type={showPasswords ? "text" : "password"}
                      value={passwordData.confirmPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                      required
                      minLength={8}
                    />
                  </div>

                  <div className="flex gap-2">
                    <Button
                      type="submit"
                      disabled={loading}
                      className="bg-gradient-to-r from-green-600 to-red-600 hover:from-green-700 hover:to-red-700"
                    >
                      {loading ? (
                        <>
                          <Loader className="w-4 h-4 mr-2 animate-spin" />
                          Changing...
                        </>
                      ) : (
                        'Update Password'
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowPasswordForm(false);
                        setPasswordData({
                          currentPassword: '',
                          newPassword: '',
                          confirmPassword: ''
                        });
                      }}
                      disabled={loading}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>
        )}

        {/* Cancel Subscription Card */}
        <Card className="border-orange-200 mb-6">
          <CardHeader>
            <CardTitle className="text-orange-600 flex items-center">
              <AlertCircle className="w-5 h-5 mr-2" />
              Subscription Management
            </CardTitle>
            <CardDescription>Cancel your subscription - you'll have access until the end of your billing period</CardDescription>
          </CardHeader>
          <CardContent>
            {!showCancelSubscription ? (
              <Button
                variant="outline"
                className="border-orange-300 text-orange-600 hover:bg-orange-50"
                onClick={() => setShowCancelSubscription(true)}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Cancel Subscription
              </Button>
            ) : (
              <div className="space-y-4">
                <div className="p-4 bg-orange-50 border border-orange-200 rounded-md">
                  <p className="text-sm text-orange-800 font-medium mb-2">
                    ⚠️ Are you sure you want to cancel?
                  </p>
                  <p className="text-sm text-orange-700 mb-2">
                    You'll continue to have access until the end of your current billing period.
                  </p>
                  <p className="text-sm text-orange-700">
                    You can resubscribe anytime to regain full access.
                  </p>
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    className="border-orange-300 text-orange-600 hover:bg-orange-50"
                    onClick={handleCancelSubscription}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Loader className="w-4 h-4 mr-2 animate-spin" />
                        Cancelling...
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4 mr-2" />
                        Yes, Cancel Subscription
                      </>
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => setShowCancelSubscription(false)}
                    disabled={loading}
                  >
                    Keep Subscription
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Delete Account Card */}
        <Card className="border-red-200">
          <CardHeader>
            <CardTitle className="text-red-600 flex items-center">
              <AlertCircle className="w-5 h-5 mr-2" />
              Danger Zone
            </CardTitle>
            <CardDescription>Permanently delete your account and all data</CardDescription>
          </CardHeader>
          <CardContent>
            {!showDeleteConfirm ? (
              <Button
                variant="destructive"
                onClick={() => setShowDeleteConfirm(true)}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Account
              </Button>
            ) : (
              <div className="space-y-4">
                <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-800 font-medium mb-2">
                    ⚠️ Warning: This action cannot be undone!
                  </p>
                  <p className="text-sm text-red-700">
                    All your data including vocabulary progress, game scores, and account information will be permanently deleted.
                  </p>
                </div>

                {user.auth_provider === 'email' && (
                  <div className="space-y-2">
                    <Label htmlFor="deletePassword">
                      Confirm your password to delete account
                    </Label>
                    <Input
                      id="deletePassword"
                      type="password"
                      value={deletePassword}
                      onChange={(e) => setDeletePassword(e.target.value)}
                      placeholder="Enter your password"
                    />
                  </div>
                )}

                <div className="flex gap-2">
                  <Button
                    variant="destructive"
                    onClick={handleDeleteAccount}
                    disabled={loading || (user.auth_provider === 'email' && !deletePassword)}
                  >
                    {loading ? (
                      <>
                        <Loader className="w-4 h-4 mr-2 animate-spin" />
                        Deleting...
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4 mr-2" />
                        Yes, Delete My Account
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowDeleteConfirm(false);
                      setDeletePassword('');
                    }}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Profile;
